import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { flatMapSuperDeep } from "@ankaadia/ankaadia-shared";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { Document, DocumentMode, StaticDataType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { compact, isEmpty, isNil, pick, sumBy } from "lodash";
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from "primeng/accordion";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { map, Observable, of, switchMap } from "rxjs";
import { DocumentsService } from "../../features/documents/documents.service";
import { CandidateProfileDialogService } from "../../organization-specific/base-candidate-profile/candidate-profile-dialog.service";
import { StaticDataService } from "../../shared/static-data/static-data.service";

@Component({
  selector: "new-ui-formly-menu-bar",
  templateUrl: "./new-ui-formly-menu-bar.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, Accordion, AccordionPanel, AccordionHeader, AccordionContent, NewUiButton, Menu],
})
export class NewUiFormlyMenuBarComponent extends FieldType implements OnInit {
  private readonly language = this.transloco.getActiveLang();

  protected readonly storageKey = "remembered-menu-item";
  protected readonly isNil = isNil;

  protected invalidity: number[];
  protected uploadMenu: MenuItem[];

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly documentService: DocumentsService,
    private readonly profileDialogService: CandidateProfileDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setUploadMenu();
  }

  protected openUploadDialog(documentType: string): void {
    this.documentService
      .getByType(documentType, this.formState.organizationId, this.formState.candidateId)
      .pipe(
        switchMap((document) => (document ? of(document) : this.createDocument(documentType))),
        map((document) => ({
          document,
          candidateForm: this.form,
          showDocumentCompletion: this.props.showDocumentCompletion,
        }))
      )
      .subscribe((event) => this.profileDialogService.documentDialog.next(event));
  }

  private createDocument(type: string): Observable<Document> {
    return this.documentService.createEmptyDocument(
      DocumentMode.Candidate,
      this.formState.organizationId,
      this.formState.candidateId,
      null,
      type
    );
  }

  private setUploadMenu(): void {
    if (!this.props.uploadTypes) return;

    if (this.formState.candidateId) {
      this.staticDataService
        .getStaticData(
          StaticDataType.AllowedUploadFileTypes,
          this.language,
          pick(this.formState, "candidateId", "organizationId")
        )
        .subscribe((data) => {
          this.uploadMenu = this.props.uploadTypes?.map((documentType) => ({
            label: data.find((d) => d.value == documentType)?.label,
            command: (): void => this.openUploadDialog(documentType),
          }));
          this.changeDetector.markForCheck();
        });
    } else {
      this.uploadMenu = null;
    }
  }

  private errorCount(field: FormlyFieldConfig): number {
    return field.fieldGroup?.length && !field.fieldArray
      ? sumBy(field.fieldGroup, (f) => this.errorCount(f))
      : field.formControl.invalid && (!!field.formControl.errors || this.hasNestedErrors(field))
        ? 1
        : 0;
  }

  // errors are not always propagated to the parent field, hence we also check the nested fields for errors
  private hasNestedErrors(field: FormlyFieldConfig): boolean {
    const nestedErrors = flatMapSuperDeep(field?.fieldGroup, (x) => x?.fieldGroup).map((x) => x?.formControl?.errors);
    return !isEmpty(compact(nestedErrors));
  }
}
